import React, { useEffect, useState } from 'react';

import { ScannerPropertiesErrorCode } from '@rtkwlf/rendall';
import upperFirst from 'lodash/upperFirst';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Grid, Skeleton, Typography } from '@packages/fenrir/components';
import { Stack } from '@packages/fenrir/components/stack';
import { useOpen } from '@packages/hooks/use-open';

import { useEnhancedScannerDetails } from '../../hooks/use-enhanced-scanner-details/use-enhanced-scanner-details';
import { useScannerConfigurationContext } from '../../pages/scanners/pages/scanners-config/components/scanner-configuration-tabs/scanner-configuration-context-provider';

import { ScannerNameControl } from './scanner-name-control/scanner-name-control';
import { ScannerNameField } from './scanner-name-field/scanner-name-field';

export type ScannerInformationProps = {
  onEditModeChange: (newMode: boolean) => void;
  onScannerNameUpdate: (scannerName: string) => void;
  onResetNameField: () => void;
  scannerNameInForm: string;
};

const TextSkeleton = () => <Skeleton variant="text" sx={{ width: '10rem' }} />;

export const ScannerInformation: React.FC<ScannerInformationProps> = ({
  onEditModeChange,
  onScannerNameUpdate,
  onResetNameField,
  scannerNameInForm,
}) => {
  const { t } = useTranslation('telemetryScannerConfig');
  const { scannerId = '' } = useParams();
  const { data: scannerDetails, isPending } =
    useEnhancedScannerDetails(scannerId);

  const [scannerNameFieldValue, setScannerNameFieldValue] =
    useState<string>('');

  const [nameFieldError, setNameFieldError] = useState<string>('');
  const { isFormDirty } = useScannerConfigurationContext();

  useEffect(() => {
    if (scannerDetails) {
      setScannerNameFieldValue(scannerDetails.name);
    }
  }, [scannerDetails]);

  const errorCode =
    scannerDetails?.errorCode === ScannerPropertiesErrorCode.CdPropsFail;

  const isEditable = !errorCode && Boolean(scannerDetails?.name);
  const { open, close, isOpen } = useOpen(false);

  useEffect(() => {
    onEditModeChange(isOpen);
  }, [isOpen, onEditModeChange]);

  useEffect(() => {
    if (
      scannerNameInForm !== scannerNameFieldValue &&
      !isFormDirty &&
      !isOpen
    ) {
      setScannerNameFieldValue(scannerNameInForm);
    }
  }, [scannerNameInForm, scannerNameFieldValue, isFormDirty, isOpen]);

  const handleScannerNameChange = (scannerName: string) => {
    if (!scannerName.length) {
      setNameFieldError(t('validationMessage.emptyField'));
    } else {
      setNameFieldError('');
    }
    setScannerNameFieldValue(scannerName);
  };

  return (
    <Stack
      data-testid="main-scanner-info-column"
      sx={{
        mb: 2.5,
        borderRadius: 2,
        p: 3,
        backgroundColor: 'var(--aw-palette-widget-widgetBackground)',
      }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        {/* Temporary solution to match other tabs using fenrir v1 */}
        <Typography variant="h4" sx={{ fontSize: '1rem', fontWeight: 700 }}>
          {t('scannerInformation.title')}
        </Typography>
        {isEditable && scannerDetails?.isConnected && (
          <ScannerNameControl
            editMode={isOpen}
            onEditModeChange={open}
            saveButtonDisabled={scannerNameFieldValue === ''}
            handleSave={() => {
              close();
              onScannerNameUpdate(scannerNameFieldValue);
            }}
            handleCancel={() => {
              close();
              setScannerNameFieldValue(scannerNameInForm);
              setNameFieldError('');
              onResetNameField();
            }}
          />
        )}
      </Stack>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2.5 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">
              {t('scannerInformation.scannerName')}
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <ScannerNameField
                scannerNameFieldValue={scannerNameFieldValue}
                editable={isEditable}
                editMode={isOpen}
                onScannerNameChange={handleScannerNameChange}
                errorMessage={nameFieldError}
              />
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2.5 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">
              {t('scannerInformation.scannerUuid')}
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">{scannerDetails?.id}</Typography>
            )}
          </Stack>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2.5 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">
              {t('scannerInformation.productType')}
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">
                {upperFirst(scannerDetails?.type)}
              </Typography>
            )}
          </Stack>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2.5 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">
              {t('scannerInformation.version')}
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">{scannerDetails?.version}</Typography>
            )}
          </Stack>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2.5 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">
              {t('scannerInformation.ip')}
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">{scannerDetails?.ip}</Typography>
            )}
          </Stack>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2.5 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">
              {t('scannerInformation.netmask')}
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">{scannerDetails?.netmask}</Typography>
            )}
          </Stack>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2.5 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">
              {t('scannerInformation.deploymentId')}
            </Typography>
            {isPending ? (
              <TextSkeleton />
            ) : (
              <Typography variant="body2">
                {scannerDetails?.deploymentID}
              </Typography>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};
